import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Button from "@/components/ui/button";
import Chip from "@/components/ui/chip";
import Popover from "@/components/ui/popover";
import Dropdown from "@/components/ui/dropdown";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import Switch from "@/components/ui/switch";
import FileInput from "@/components/ui/file-input";
import Modal from "@/components/ui/modal";
import Drawer from "@/components/ui/drawer";
import ConfirmModal from "@/components/ui/confirm-modal";
import { Actions as ActionsIcon, Edit, Delete, Check, Close } from "@/components/icons";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import { ALLOWED_FILE_TYPE_PRESETS } from "@/types/file-upload.types";
import { getFileDownloadUrl } from "@/lib/files.api";
import {
  examManagementApi,
  getQuestionDifficultyLabel,
  normalizeQuestionDifficulty,
  type QuestionPoolItem,
  type QuestionDifficulty,
} from "../services/exam-management.api";
import styles from "./QuestionPoolPage.module.scss";

type OptionState = { optionText: string; isCorrect: boolean };

const difficultyOptions = [
  { label: "Kolay", value: "EASY" as QuestionDifficulty },
  { label: "Orta", value: "MEDIUM" as QuestionDifficulty },
  { label: "Zor", value: "HARD" as QuestionDifficulty },
];

const difficultyChipType: Record<QuestionDifficulty, "default" | "error" | "info" | "primary" | "secondary" | "success" | "warning"> = {
  EASY: "success",
  MEDIUM: "warning",
  HARD: "error",
};

const emptyOption = (): OptionState => ({ optionText: "", isCorrect: false });

const emptyQuestion = () => ({
  questionText: "",
  difficulty: "MEDIUM" as QuestionDifficulty,
  allowMultipleSelection: false,
  imageFileID: undefined as string | undefined,
  imagePreview: undefined as string | undefined,
  options: [emptyOption(), emptyOption()],
});

const normalizeItem = (item: QuestionPoolItem) => ({
  ...item,
  options: item.options?.length ? item.options : [emptyOption(), emptyOption()],
  difficulty: normalizeQuestionDifficulty(item.difficulty),
});

const QuestionPoolPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<QuestionPoolItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState<QuestionPoolItem | null>(null);
  const [viewingItem, setViewingItem] = useState<QuestionPoolItem | null>(null);
  const [viewImageUrl, setViewImageUrl] = useState<string | undefined>(undefined);
  const [deletingItem, setDeletingItem] = useState<QuestionPoolItem | null>(null);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
  const [form, setForm] = useState(emptyQuestion());

  const openCreateModal = () => {
    navigate("/dashboard/exam-management/question-pool/create");
  };

  const openEditModal = (item: QuestionPoolItem) => {
    setEditingItem(item);
  };

  const openViewModal = (item: QuestionPoolItem) => {
    setViewingItem(item);
  };

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const loadItems = async (page = 1, limit = pageSize) => {
    setLoading(true);
    try {
      const result = await examManagementApi.getQuestionPool({ page, limit });
      setItems(result.items.map(normalizeItem));
      setCurrentPage(result.page);
      setTotalRecords(result.totalCount);
    } catch {
      toast.error("Sorular yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadItems(1, pageSize);
  }, []);

  useEffect(() => {
    if (editingItem) {
      setForm({
        questionText: editingItem.questionText,
        difficulty: normalizeQuestionDifficulty(editingItem.difficulty),
        allowMultipleSelection: editingItem.allowMultipleSelection,
        imageFileID: editingItem.imageFileID,
        imagePreview: editingItem.imagePreview,
        options: editingItem.options.length ? editingItem.options.map((opt) => ({ optionText: opt.optionText, isCorrect: opt.isCorrect })) : [emptyOption(), emptyOption()],
      });
      if (editingItem.imageFileID) {
        void getFileDownloadUrl(editingItem.imageFileID)
          .then((downloadUrl) => {
            setForm((prev) => ({ ...prev, imagePreview: downloadUrl }));
          })
          .catch(() => {
            setForm((prev) => ({ ...prev, imagePreview: undefined }));
          });
      }
    }
  }, [editingItem]);

  useEffect(() => {
    let mounted = true;
    if (!viewingItem?.imageFileID) {
      setViewImageUrl(undefined);
      return;
    }
    void getFileDownloadUrl(viewingItem.imageFileID)
      .then((downloadUrl) => {
        if (mounted) setViewImageUrl(downloadUrl);
      })
      .catch(() => {
        if (mounted) setViewImageUrl(undefined);
      });
    return () => {
      mounted = false;
    };
  }, [viewingItem]);

  const canSubmit = useMemo(() => {
    const validOptions = form.options.filter((opt) => opt.optionText.trim());
    return form.questionText.trim().length > 0 && validOptions.length >= 2 && validOptions.some((opt) => opt.isCorrect);
  }, [form]);

  const updateOption = (index: number, patch: Partial<OptionState>) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((opt, i) => (i === index ? { ...opt, ...patch } : opt)),
    }));
  };

  const addOption = () => setForm((prev) => ({ ...prev, options: [...prev.options, emptyOption()] }));
  const removeOption = (index: number) => setForm((prev) => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }));
  const toggleCorrectOption = (index: number) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((opt, i) => ({
        ...opt,
        isCorrect: prev.allowMultipleSelection ? (i === index ? !opt.isCorrect : opt.isCorrect) : i === index,
      })),
    }));
  };

  const submitForm = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        questionText: form.questionText.trim(),
        difficulty: form.difficulty,
        allowMultipleSelection: form.allowMultipleSelection,
        imageFileID: form.imageFileID,
        options: form.options
          .map((opt) => ({ optionText: opt.optionText.trim(), isCorrect: opt.isCorrect }))
          .filter((opt) => opt.optionText.length > 0),
      };
        if (!editingItem) return;
        await examManagementApi.updateQuestionPoolItem(editingItem.id, payload);
        toast.success("Soru güncellendi");
        setEditingItem(null);
        await loadItems();
    } catch {
      toast.error("İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async () => {
    if (!deletingItem) return;
    setSaving(true);
    try {
      await examManagementApi.deleteQuestionPoolItem(deletingItem.id);
      toast.success("Soru silindi");
      setDeletingItem(null);
      await loadItems();
    } catch {
      toast.error("Soru silinemedi");
    } finally {
      setSaving(false);
    }
  };

  const tableColumns = useMemo<TableColumn<QuestionPoolItem>[]>(() => [
    {
      header: "Soru",
      className: "question-column",
      accessor: (row) => {
        const correctCount = row.options.filter((opt) => opt.isCorrect).length;
        return (
          <div className={styles.questionCell}>
            <span className={styles.questionTitle}>{row.questionText}</span>
            <span className={styles.questionMeta}>
              {row.options.length} seçenek · {correctCount} doğru
            </span>
            {row.imageFileID ? <span className={styles.imageMeta}>Görsel eklendi</span> : null}
          </div>
        );
      },
    },
    {
      header: "Zorluk",
      className: "difficulty-column",
      accessor: (row) => {
        const difficulty = normalizeQuestionDifficulty(row.difficulty);
        return (
          <Chip type={difficultyChipType[difficulty]} variant="filled" size="small">
            {getQuestionDifficultyLabel(difficulty)}
          </Chip>
        );
      },
    },
    {
      header: "Seçim Tipi",
      className: "selection-type-column",
      accessor: (row) => (
        <Chip type={row.allowMultipleSelection ? "info" : "default"} variant="filled" size="small">
          {row.allowMultipleSelection ? "Çoklu" : "Tekli"}
        </Chip>
      ),
    },
    {
      header: "Seçenek Özeti",
      className: "option-summary-column",
      accessor: (row) => (
        <span className={styles.optionSummary}>
          {row.options.slice(0, 2).map((opt) => opt.optionText).join(" · ") || "-"}
        </span>
      ),
    },
    {
      header: "Görsel",
      className: "image-column",
      accessor: (row) => (
        <Chip type={row.imageFileID ? "success" : "default"} variant="filled" size="small">
          {row.imageFileID ? "Var" : "Yok"}
        </Chip>
      ),
    },
  ], []);

  const renderActions = (row: QuestionPoolItem) => {
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;

    const menuContent = (
      <div className={styles.actionMenu}>
        <button
          type="button"
          className={styles.actionMenuItem}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, false);
            openEditModal(row);
          }}
        >
          <Edit width={18} height={18} />
          Düzenle
        </button>
        <button
          type="button"
          className={styles.actionMenuItem}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, false);
            setDeletingItem(row);
          }}
        >
          <Delete width={18} height={18} />
          Sil
        </button>
      </div>
    );

    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          type="button"
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <Header title="Sorular" />

      <PageHeader onAddNew={openCreateModal} addButtonLabel="Yeni Soru Ekle" />

      <Table
        columns={tableColumns}
        data={items}
        emptyMessage="Henüz soru yok. Sağ üstten yeni soru oluştur."
        hasVisibleActions
        getRowId={(row) => row.id}
        isLoading={loading}
        onRowClick={openViewModal}
        currentPage={currentPage}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={(page) => {
          void loadItems(page, pageSize);
        }}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
          void loadItems(1, size);
        }}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
        actionsColumnHeader="İşlemler"
      />

      <Modal
        isOpen={!!editingItem}
        onClose={() => { setEditingItem(null); }}
        title="Soruyu Düzenle"
        size="lg"
        loading={saving}
        customFooter={(
          <>
            <Button variant="text" color="secondary" onClick={() => { setEditingItem(null); }}>
              İptal
            </Button>
            <Button variant="contained" color="primary" onClick={() => { void submitForm(); }} loading={saving} disabled={!canSubmit}>
              Güncelle
            </Button>
          </>
        )}
      >
        <div className={styles.editCard}>
          <div className={styles.form}>
            <Textarea label="Soru" value={form.questionText} onChange={(e) => setForm((prev) => ({ ...prev, questionText: e.target.value }))} />
            <div className={styles.rowTwo}>
              <Dropdown
                label="Zorluk"
                options={difficultyOptions.map((option) => ({ label: option.label, value: option.value }))}
                selectedLabel={getQuestionDifficultyLabel(form.difficulty)}
                value={form.difficulty}
                onSelect={(value) => {
                  const selected = difficultyOptions.find((option) => option.label === value || option.value === value);
                  if (selected) setForm((prev) => ({ ...prev, difficulty: selected.value }));
                }}
              />
              <label className={styles.switchRow}>
                <Switch checked={form.allowMultipleSelection} onChange={(checked) => setForm((prev) => ({ ...prev, allowMultipleSelection: checked }))} />
                Çoklu seçim
              </label>
            </div>
            <FileInput
              label="Soru görseli"
              placeholder="Soru için görsel yükleyin"
              accept={ALLOWED_FILE_TYPE_PRESETS.IMAGE.map((f) => `.${f.toLowerCase()}`).join(",")}
              supportedFormats={ALLOWED_FILE_TYPE_PRESETS.IMAGE}
              maxSize={10}
              maxFiles={1}
              fileType="image"
              uploadToBackend
              enableDemoProgress={false}
              existingFileUrl={form.imagePreview}
              onUploadSuccess={(fileId, previewUrl) => setForm((prev) => ({ ...prev, imageFileID: fileId, imagePreview: previewUrl }))}
              onFileRemove={() => setForm((prev) => ({ ...prev, imageFileID: undefined, imagePreview: undefined }))}
            />
            <div className={styles.optionList}>
              {form.options.map((opt, index) => (
                <div className={`${styles.answerOptionRow} ${opt.isCorrect ? styles.answerOptionRowSelected : ""}`} key={index}>
                  <button type="button" className={styles.answerRadio} onClick={() => toggleCorrectOption(index)}>
                    {opt.isCorrect && <Check width={14} height={14} color="#15AC77" />}
                  </button>
                  <div className={styles.answerOptionInputWrap}>
                    <Input
                      placeholder={`Seçenek ${index + 1}`}
                      value={opt.optionText}
                      onChange={(e) => updateOption(index, { optionText: e.target.value })}
                    />
                  </div>
                  {form.options.length > 2 && (
                    <button type="button" className={styles.removeOptionBtn} onClick={() => removeOption(index)}>
                      <Close width={18} height={18} color="#707a8f" border={false} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <Button variant="outlined" color="default" onClick={addOption}>Seçenek Ekle</Button>
          </div>
        </div>
      </Modal>

      <Drawer
        isOpen={!!viewingItem}
        onClose={() => { setViewingItem(null); setViewImageUrl(undefined); }}
        title="Soru Detayı"
      >
        {viewingItem && (
          <div className={styles.viewCard}>
            <div className={styles.viewBlock}>
              <span className={styles.viewLabel}>Soru</span>
              <p className={styles.viewValue}>{viewingItem.questionText}</p>
            </div>
            <div className={styles.viewBlock}>
              <span className={styles.viewLabel}>Görsel</span>
              {viewImageUrl ? (
                <img src={viewImageUrl} alt="Soru görseli" className={styles.viewImage} />
              ) : (
                <span className={styles.viewValue}>{viewingItem.imageFileID ? "Görsel yüklenmiş (önizleme alınamadı)" : "Görsel yok"}</span>
              )}
            </div>
            <div className={styles.viewMetaRow}>
              <div className={styles.viewBlock}>
                <span className={styles.viewLabel}>Zorluk</span>
                <Chip type={difficultyChipType[normalizeQuestionDifficulty(viewingItem.difficulty)]} variant="filled" size="small">
                  {getQuestionDifficultyLabel(viewingItem.difficulty)}
                </Chip>
              </div>
              <div className={styles.viewBlock}>
                <span className={styles.viewLabel}>Seçim tipi</span>
                <span className={styles.viewValue}>{viewingItem.allowMultipleSelection ? "Çoklu seçim" : "Tekli seçim"}</span>
              </div>
            </div>
            <div className={styles.viewBlock}>
              <span className={styles.viewLabel}>Seçenekler</span>
              <div className={styles.viewOptions}>
                {viewingItem.options.map((opt, idx) => (
                  <div key={`${viewingItem.id}-${idx}`} className={`${styles.viewOptionItem} ${opt.isCorrect ? styles.viewOptionItemCorrect : ""}`}>
                    <span>{opt.optionText || `Seçenek ${idx + 1}`}</span>
                    {opt.isCorrect && <Check width={14} height={14} color="#15AC77" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Drawer>

      <ConfirmModal
        isOpen={!!deletingItem}
        onClose={() => setDeletingItem(null)}
        onConfirm={() => { void deleteItem(); }}
        title="Soruyu sil"
        message={deletingItem?.questionText ?? ""}
        confirmText="Sil"
        confirmVariant="danger"
        loading={saving}
      />
    </DashboardLayout>
  );
};

export default QuestionPoolPage;
